import React from 'react';
import AboutYourAbsenceText from '../constants/AboutYourAbsenceText';
import { validationDisplayTextEs } from 'src/utils/language/validationDisplayTextEs';
import { validationDisplayTextEn } from 'src/utils/language/validationDisplayTextEn';
import LanguageToggle from 'src/utils/LanguageToggle';
import { addDays, addYears, isAfter, isBefore, parse } from 'date-fns';
import { ElectionConstants } from '../constants/ElectionConstantsText';
import { GetCurrentDate } from 'src/packages/lci/constants/LCIConstant';
import { fieldInvalidMsg, fieldErrorMsg, nameInvalidMsg, resolveLabel } from 'src/utils/validationMessages';
export const validateCliNameOptional = (name, label) => {
    const regex = RegExp(/^([-\\.'a-zA-Z]|^[-\\.'a-zA-Z][-\\.' a-zA-Z]{0,33}[-\\.'a-zA-Z])$/gm);
    if (name && !regex.test(name)) {
        return fieldInvalidMsg(label);
    }
    return undefined;
};
export const validateName = (name, label, t) => {
    const regex = RegExp(/^([-\\.'a-zA-Z]|^[-\\.'a-zA-Z][-\\.' a-zA-Z]{0,33}[-\\.'a-zA-Z])$/gm);
    if (!name) {
        return undefined;
    }
    if (name && !regex.test(name)) {
        return nameInvalidMsg(label);
    }
    return undefined;
};
export const validateEmail = (email, label) => {
    const regex = RegExp(/^([0-9a-zA-Z]([-\.\w]*[0-9a-zA-Z])*@([0-9a-zA-Z]{0,}[-\w]*[0-9a-zA-Z]\.)+[a-zA-Z]{2,9})$/i);
    if (email && (!email.match(regex) || email.length >= 140)) {
        return label ? fieldInvalidMsg(label) : <LanguageToggle en={validationDisplayTextEn.invalidEmail} es={validationDisplayTextEs.invalidEmail}/>;
    }
    return undefined;
};
export const validateOptionalEmail = (email, label) => {
    const regex = RegExp(/^([0-9a-zA-Z]([-\.\w]*[0-9a-zA-Z])*@([0-9a-zA-Z]{0,}[-\w]*[0-9a-zA-Z]\.)+[a-zA-Z]{2,9})$/i);
    if (email && (!email.match(regex) || email.length >= 140)) {
        return label ? fieldInvalidMsg(label) : <LanguageToggle en={validationDisplayTextEn.invalidEmail} es={validationDisplayTextEs.invalidEmail}/>;
    }
    return undefined;
};
export const validateOtherRelationship = (relationship, label, t) => {
    const regex = RegExp(/^([ a-zA-Z]{0,34})$/gm);
    if (relationship && !regex.test(relationship)) {
        return fieldErrorMsg(label, AboutYourAbsenceText.formLabels.relationshipError.english, AboutYourAbsenceText.formLabels.relationshipError.spanish);
    }
    return undefined;
};
export const validateTextArea = ({ value, t, charLimit = 0 }) => {
    if (!value) {
        return undefined;
    }
    const test = `^[-a-zA-Z0-9' .,:;_#/!*+=?&<>$@%(){}\\s]{0,${charLimit}}$`;
    const regex = new RegExp(test);
    if (!regex.test(value)) {
        return t(AboutYourAbsenceText.formLabels.textAreaError)(charLimit);
    }
};
export const validateJobRequirementTextArea = (value, t) => {
    //field must be less than 250 characters and may contain only numbers and letters
    const regex = /^[a-zA-Z0-9 ]{0,250}$/;
    if (!regex.test(value)) {
        return t(AboutYourAbsenceText.formLabels.textAreaError)(250);
    }
};
export const validateZeroToOneHundred = (value, label) => {
    const regex = /^[0-9][0-9]?$|^100$/;
    if ((value && value.length > 3) || (value && !regex.test(value))) {
        return fieldErrorMsg(label, 'must be between 0 and 100.', 'debe estar entre 0 y 100.');
    }
};
export const rangedNumberValidation = (value, startRange, endRange, label) => {
    return value && (isNaN(Number(value)) || parseInt(value) < startRange || parseInt(value) > endRange)
        ? fieldErrorMsg(label, `must be a number between ${startRange} and ${endRange}.`, `debe ser un número entre ${startRange} y ${endRange}.`)
        : undefined;
};
export const validateDateIsWithinAYear = (input, t, label, required) => {
    const l = resolveLabel(label);
    const date = parse(input, 'P', new Date());
    if (isBefore(date, addYears(new Date(), -1)) || isAfter(date, addYears(new Date(), 1))) {
        return <LanguageToggle en={`${l.en} must be within one year of today's date`} es={`${l.es} debe estar dentro de un año de la fecha de hoy`}/>;
    }
};
export const validateDateIsWithinOneYearBeforeAndTwoAfterToday = (input, t, label) => {
    const l = resolveLabel(label);
    const date = parse(input, 'P', new Date());
    if (isBefore(date, addYears(addDays(new Date(), -1), -1))) {
        return t({
            english: `${l.en} must be on or after one year before today's date`,
            spanish: `${l.es} debe ser igual o posterior a un año antes de la fecha de hoy`,
        });
    }
    else if (isAfter(date, addYears(new Date(), 2))) {
        return t({
            english: `${l.en} must be within two years of today's date`,
            spanish: `${l.es} debe estar dentro de los dos años de la fecha de hoy`,
        });
    }
};
export const validateDateIsWithinAYearFromToday = (input, t, label) => {
    const l = resolveLabel(label);
    const date = parse(input, 'P', new Date());
    if (isAfter(new Date(date), addYears(new Date(), 1))) {
        return <LanguageToggle en={`${l.en} cannot occur more than one year after today's date`} es={`${l.es} no puede ocurrir más de un año después de la fecha de hoy`}/>;
    }
};
export const validateDateIsOnOrBeforeToday = (input, t, label) => {
    const l = resolveLabel(label);
    const today = new Date();
    const minDate = new Date('01/01/1900');
    const inputDate = parse(input, 'P', new Date());
    if (input && inputDate > today) {
        return t({
            english: `${l.en} must be on or before today's date`,
            spanish: `${l.es} debe ser igual o anterior a la fecha de hoy`,
        });
    }
    else if (input && inputDate < minDate) {
        return t({
            english: `${l.en} must be between 01/01/1900 and ${GetCurrentDate()}`,
            spanish: `${l.es} debe estar entre 01/01/1900 y ${GetCurrentDate()}`,
        });
    }
};
export const validatePostalCode = (postalCode, claimantData, label) => {
    if (!postalCode)
        return undefined; //let honest handle it
    //@ts-ignore
    const selectedCountryID = claimantData.claimantData.selectedCountryID;
    const canadaPostalCodeRegex = /^[A-Za-z][0-9][A-Za-z] ?[0-9][A-Za-z][0-9]$/;
    const usaPostalCodeRegex = /^\d{5}(?:[-]\d{4})?$/;
    if (selectedCountryID === 'USA') {
        if (!postalCode.match(usaPostalCodeRegex))
            return fieldInvalidMsg(label);
    }
    else if (selectedCountryID === 'CAN') {
        if (!canadaPostalCodeRegex.test(postalCode))
            return fieldInvalidMsg(label);
    }
    else if (postalCode?.length < 1) {
        return fieldInvalidMsg(label);
    }
};
export const validateNameDD = (name, label) => {
    const regex = RegExp(/^[-A-Za-z'. ]*$/gm);
    if (name && !regex.test(name)) {
        return <LanguageToggle en={ElectionConstants.name.english} es={ElectionConstants.name.spanish}/>;
    }
    return undefined;
};
export const validateRoute = (route, label) => {
    if (route && route.length < 9) {
        return fieldErrorMsg(label, 'must be at least 9 digits.', 'debe tener al menos 9 dígitos.');
    }
    return undefined;
};
export const accountrouteMask = (rvalue) => {
    if (rvalue) {
        const numbers = rvalue.toString().replace(/[^\d]/g, '');
        return numbers;
    }
    else {
        return '';
    }
};
export const validateConfirmAccount = (confirmAccountNumber, values, label) => {
    const accountNumber = values.accountNumber ? values.accountNumber : '';
    if (confirmAccountNumber && confirmAccountNumber.length < 4) {
        return fieldErrorMsg(label, 'must be at least 4 digits.', 'debe tener al menos 4 dígitos.');
    }
    if (accountNumber !== confirmAccountNumber) {
        return fieldErrorMsg(label, 'does not match.', 'no coincide.');
    }
    return undefined;
};
export const validateAccount = (route, label) => {
    if (route && route.length < 4) {
        return fieldErrorMsg(label, 'must be at least 4 digits.', 'debe tener al menos 4 dígitos.');
    }
    return undefined;
};
export const validateAddress1 = (address, label) => {
    if (address && !address.match(/^[0-9A-Za-z .,?#'()\-/]{1,30}$/)) {
        return fieldErrorMsg(label, 'must be between 0 and 30 characters.', 'debe estar entre 0 y 30 caracteres.');
    }
    return undefined;
};
export const validateAddress2 = (address, label) => {
    if (address && !address.match(/^[0-9A-Za-z .,?#'()\-/]{1,30}$/)) {
        return fieldErrorMsg(label, 'must be between 0 and 30 characters.', 'debe estar entre 0 y 30 caracteres.');
    }
    return undefined;
};
